import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

interface BarraDeGuardadoProps {
  /** Cuántos campos del formulario están sucios. */
  cambios: number;
  enviando: boolean;
  /** Frena el envío sin pintar el spinner (ver `bloqueado` del formulario). */
  bloqueado?: boolean;
  etiquetaEnvio: string;
  /** Id del `<form>` al que envía, cuando la barra vive FUERA de él (ver abajo). */
  formId?: string;
  /**
   * Guardar por CLIC en vez de por `<form>`: para las pestañas que no son un
   * formulario (Construcciones) y escriben desde un manejador propio. Con él, el
   * botón es `button` y `formId` no se usa.
   */
  onGuardar?: () => void;
  /** Qué se guarda solo, en la frase al lado del contador. */
  ayuda?: string;
  onDescartar: () => void;
}

/**
 * La barra fija al pie de "Antecedentes generales": dice cuánto hay sin
 * guardar y ofrece las dos salidas.
 *
 * NO tiene lógica de guardado propia: su botón es el `submit` del `<form>` al que
 * apunta —por contención, o por `formId` cuando vive FUERA de él para poder
 * cruzar el ancho entero del panel, bajo la columna del índice—, así que pasa por la misma validación y el
 * mismo `onEnviar` que siempre. Y "Guardar cambios" NO se apaga sin cambios:
 * reenviar lo que hay es legítimo, y la guardia de salida ya depende de que el
 * botón responda.
 */
export function BarraDeGuardado({
  cambios,
  enviando,
  bloqueado = false,
  etiquetaEnvio,
  formId,
  onGuardar,
  ayuda = "Las imágenes y las fotos se guardan solas al soltarlas.",
  onDescartar,
}: BarraDeGuardadoProps) {
  const hayCambios = cambios > 0;

  return (
    <div className="bg-background/95 border-border sticky bottom-0 z-5 -mx-6 flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t-[1.5px] px-6 py-3 backdrop-blur lg:-mx-8 lg:px-8">
      <span
        className={cn(
          "inline-flex items-center gap-2 text-xs font-bold",
          hayCambios ? "text-warning-soft-foreground" : "text-ink-muted",
        )}
      >
        <span
          aria-hidden="true"
          className={cn("size-2 flex-none rounded-full", hayCambios ? "bg-warning-dot" : "bg-ink-ghost")}
        />
        {hayCambios
          ? `${cambios} ${cambios === 1 ? "cambio" : "cambios"} sin guardar`
          : "Sin cambios pendientes"}
      </span>
      <span className="text-ink-hint text-[0.71875rem] font-semibold">
        {ayuda}
      </span>

      <div className="ml-auto flex flex-none items-center gap-2">
        <Button type="button" variant="outline" disabled={!hayCambios} onClick={onDescartar}>
          Descartar cambios
        </Button>
        <Button
          type={onGuardar ? "button" : "submit"}
          form={onGuardar ? undefined : formId}
          disabled={enviando || bloqueado}
          onClick={onGuardar}
        >
          {enviando ? <Spinner /> : null}
          {etiquetaEnvio}
        </Button>
      </div>
    </div>
  );
}
